(() => {
  // Shared by service pages, calculators, playbooks and native FAQ sections.
  const selector = '.bc-service__faqs details, .bc-scope details, .collapsible-content details';
  const states = new WeakMap();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  const getState = details => {
    if (!states.has(details)) {
      states.set(details, {
        expanded: details.open,
        animation: null,
        height: details.style.height,
        overflow: details.style.overflow
      });
    }
    return states.get(details);
  };

  const finish = (details, state) => {
    details.open = state.expanded;
    state.animation?.cancel();
    state.animation = null;
    details.style.height = state.height;
    details.style.overflow = state.overflow;
    details.dataset.bcDisclosureState = state.expanded ? 'open' : 'closed';
    details.dataset.bcDisclosureExpanded = String(state.expanded);
    details.querySelector('summary').setAttribute('aria-expanded', String(state.expanded));
  };

  const setExpanded = (details, expanded) => {
    const summary = details.querySelector('summary');
    const state = getState(details);
    // Read the current animated size before cancelling so reversals do not jump.
    const start = details.getBoundingClientRect().height;
    state.animation?.cancel();
    state.animation = null;
    state.expanded = expanded;
    details.dataset.bcDisclosureExpanded = String(expanded);
    summary.setAttribute('aria-expanded', String(expanded));

    if (reduced.matches || typeof details.animate !== 'function') {
      finish(details, state);
      return;
    }

    // Measure the natural open height; release it again when the animation ends
    // so translated copy, font loading and responsive wrapping stay unconstrained.
    details.style.height = state.height;
    details.open = true;
    const styles = getComputedStyle(details);
    const frame = ['borderTopWidth', 'borderBottomWidth', 'paddingTop', 'paddingBottom']
      .reduce((total, property) => total + (parseFloat(styles[property]) || 0), 0);
    const end = expanded ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height + frame;
    details.style.height = `${start}px`;
    details.style.overflow = 'hidden';
    details.dataset.bcDisclosureState = expanded ? 'opening' : 'closing';

    const animation = details.animate({height: [`${start}px`, `${end}px`]}, {
      duration: 400,
      easing: 'cubic-bezier(.22,1,.36,1)',
      fill: 'both'
    });
    state.animation = animation;
    animation.onfinish = () => {
      if (state.animation === animation) finish(details, state);
    };
  };

  const initialize = root => {
    root.querySelectorAll(selector).forEach(details => {
      if (states.has(details)) return;
      finish(details, getState(details));
    });
  };

  // Delegation also covers sections loaded later in Shopify's theme editor.
  document.addEventListener('click', event => {
    const summary = event.target.closest('summary');
    const details = summary?.parentElement;
    if (!details?.matches(selector) || event.target.closest('a, button, input, select, textarea')) return;
    event.preventDefault();
    setExpanded(details, !getState(details).expanded);
  });

  document.addEventListener('keyup', event => {
    if (event.key !== 'Escape') return;
    const details = event.target.closest('details');
    if (!details?.matches(selector) || !getState(details).expanded) return;
    event.stopPropagation();
    setExpanded(details, false);
    details.querySelector('summary').focus({preventScroll: true});
  }, true);

  document.addEventListener('toggle', event => {
    const details = event.target;
    if (!details.matches?.(selector)) return;
    const state = getState(details);
    // Respect native/programmatic toggles without letting a stale animation reopen it.
    if (!state.animation || !details.open) {
      state.expanded = details.open;
      finish(details, state);
    }
  }, true);

  document.addEventListener('shopify:section:load', event => initialize(event.target));
  initialize(document);
})();
